
import React, { useState, useEffect } from 'react';
import { Menu } from '@/types/menu';
import { Button } from '@/components/ui/button';
import { Calendar, Share } from 'lucide-react';
import ReservationModal from '@/components/ReservationModal';
import TrackPhoneCall from '../features/TrackPhoneCall';
import MenuImage from '../core/MenuImage';
import ShareLanguageDropdown from '../features/ShareLanguageDropdown';
import { LanguageCode } from '@/constants/menuUITranslations';

interface SimpleMenuHeaderProps {
  menu: Menu;
  currentLanguage: LanguageCode;
  onLanguageChange: (lang: LanguageCode) => void;
  availableLanguages: LanguageCode[];
}

const SimpleMenuHeader: React.FC<SimpleMenuHeaderProps> = ({ 
  menu,
  currentLanguage,
  onLanguageChange,
  availableLanguages
}) => {
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  
  useEffect(() => {
    // Find the best banner URL available with robust verification
    let possibleBannerUrls: string[] = [];
    
    // Helper function to validate and clean URLs
    const validateAndCleanUrl = (url: any): string | null => {
      if (!url || typeof url !== 'string' || url.trim() === '') {
        return null;
      }
      
      try {
        // Validate URL format
        const urlObj = new URL(url);
        // Force HTTPS
        return urlObj.toString().replace('http://', 'https://');
      } catch (e) {
        console.error('Invalid URL format:', url);
        return null;
      }
    };
    
    // Check each potential source and ensure it's valid
    const bannerFromMenu = validateAndCleanUrl(menu.banner_url);
    if (bannerFromMenu) possibleBannerUrls.push(bannerFromMenu);
    
    const coverFromMenu = validateAndCleanUrl(menu.cover_url);
    if (coverFromMenu) possibleBannerUrls.push(coverFromMenu);
    
    // Verification for nested custom_css properties
    if (menu.custom_css && typeof menu.custom_css === 'object') {
      const customCss = menu.custom_css as any;
      
      const customBannerUrl = validateAndCleanUrl(customCss.bannerUrl);
      if (customBannerUrl) possibleBannerUrls.push(customBannerUrl);
      
      const customCoverUrl = validateAndCleanUrl(customCss.coverUrl);
      if (customCoverUrl) possibleBannerUrls.push(customCoverUrl);
    }
    
    // Filter out any null values
    possibleBannerUrls = possibleBannerUrls.filter(Boolean);
    
    console.log('SimpleMenuHeader: Available banner URLs:', possibleBannerUrls);
    
    if (possibleBannerUrls.length > 0) {
      // Use the first URL available with a cache buster
      try {
        const selectedUrl = possibleBannerUrls[0];
        const urlObj = new URL(selectedUrl);
        
        // Add cache buster to always bypass browser cache
        urlObj.searchParams.set('t', Date.now().toString());
        
        const finalUrl = urlObj.toString();
        console.log('SimpleMenuHeader: Using banner URL:', finalUrl);
        setBannerUrl(finalUrl);
        
        // Preload the image to check if it's valid
        const preloadImg = new Image();
        preloadImg.onload = () => {
          console.log('SimpleMenuHeader: Banner preloaded successfully');
        };
        preloadImg.onerror = (e) => {
          console.error('SimpleMenuHeader: Banner preload failed', e);
          
          // If first URL fails, try the next one
          if (possibleBannerUrls.length > 1) {
            try {
              const backupUrl = new URL(possibleBannerUrls[1]);
              backupUrl.searchParams.set('t', Date.now().toString());
              console.log('SimpleMenuHeader: Trying backup URL:', backupUrl.toString());
              setBannerUrl(backupUrl.toString());
            } catch (err) {
              console.error('Error setting backup URL:', err);
              setBannerUrl(null);
            }
          } else {
            setBannerUrl(null);
          }
        };
        preloadImg.src = finalUrl;
        
      } catch (e) {
        console.error('Error processing banner URL:', e);
        setBannerUrl(null);
      }
    } else {
      console.log('SimpleMenuHeader: No valid banner URL found');
      setBannerUrl(null);
    }
  }, [menu]);
  
  // For debugging
  useEffect(() => {
    console.log('Menu data in SimpleMenuHeader:', {
      name: menu.name,
      banner_url: menu.banner_url,
      cover_url: menu.cover_url,
      custom_css: menu.custom_css,
      banner_status: bannerUrl ? 'Set' : 'Not set'
    });
  }, [menu, bannerUrl]);
  
  const logoImage = menu.logo_url || 
    (menu.custom_css && typeof menu.custom_css === 'object' 
      ? (menu.custom_css as any).logoUrl 
      : null);
  
  return (
    <header className="relative">
      {/* Affiche la bannière uniquement si elle existe */}
      {bannerUrl && (
        <div className="w-full h-48 sm:h-64 md:h-72 lg:h-80 xl:h-96 relative overflow-hidden lg:rounded-lg shadow-lg mb-6 bg-gray-100">
          <img 
            src={bannerUrl}
            alt={`Bannière ${menu.name || 'restaurant'}`}
            className="w-full h-full object-cover object-center transition-transform duration-300 hover:scale-105"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            onError={() => {
              setBannerUrl(null);
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-transparent"></div>
          
          {/* Logo overlay avec positionnement responsive */}
          {logoImage && (
            <div className="absolute bottom-4 left-4 lg:bottom-6 lg:left-6 bg-white rounded-full p-1 shadow-md z-10">
              <MenuImage 
                src={logoImage}
                size="thumbnail"
                alt={`${menu.name} logo`}
                className="h-16 w-16 lg:h-20 lg:w-20 rounded-full"
                aspectRatio={1}
                objectFit="contain"
                fallbackText="Logo"
              />
            </div>
          )}

          {/* Boutons d'action avec espacement responsive */}
          <div className="absolute top-4 right-4 lg:top-6 lg:right-6 flex items-center gap-2 z-10">
            {menu.restaurant_id && menu.phone && (
              <TrackPhoneCall 
                restaurantId={menu.restaurant_id}
                phone={menu.phone}
                buttonStyle={true}
                className="bg-white/80 hover:bg-white text-sm lg:text-base"
              />
            )}
            <ShareLanguageDropdown
              menuName={menu.name || 'Restaurant'}
              currentLanguage={currentLanguage}
              onLanguageChange={onLanguageChange}
              availableLanguages={availableLanguages}
            />
          </div>
        </div>
      )}

      {/* Affiche le logo seul si pas de bannière mais logo disponible */}
      {!bannerUrl && logoImage && (
        <div className="mb-6 flex justify-center">
          <div className="bg-white rounded-full p-1 shadow-md">
            <MenuImage 
              src={logoImage}
              size="thumbnail"
              alt={`${menu.name} logo`}
              className="h-20 w-20 lg:h-24 lg:w-24 rounded-full"
              aspectRatio={1}
              objectFit="contain"
              fallbackText="Logo"
            />
          </div>
        </div>
      )}

      {isReservationModalOpen && menu.restaurant_id && (
        <ReservationModal
          isOpen={isReservationModalOpen}
          onClose={() => setIsReservationModalOpen(false)}
          restaurantId={menu.restaurant_id}
          restaurantName={menu.name || "Restaurant"}
        />
      )}
    </header>
  );
};

export default SimpleMenuHeader;
